// ============================================================================
// Screening API — routine medical screening (campaigns, protocols, intake,
// cohort report). Backend routes: backend/screening/urls.py, mounted by
// core.urls at /api/screening/.
//
//   GET/POST       /api/screening/protocols/
//   PATCH/DELETE   /api/screening/protocols/{id}/
//   POST           /api/screening/protocol-items/
//   PATCH/DELETE   /api/screening/protocol-items/{id}/
//   GET/POST       /api/screening/campaigns/
//   GET/PATCH/DEL  /api/screening/campaigns/{id}/
//   POST           /api/screening/campaigns/{id}/start_encounter/
//   GET            /api/screening/campaigns/{id}/cohort_report/
//   GET            /api/screening/records/?campaign={id}
//   PATCH          /api/screening/records/{id}/
//
// The Fitness for Work PDF is produced through the shared document
// generator (POST /api/documents/generate/).
// ============================================================================

import { api } from './client.js'
import { unwrapList } from '../utils/format.js'

// ---- Protocols ----

export async function listScreeningProtocols() {
  const { data } = await api.get('/api/screening/protocols/', {
    params: { page_size: 200 },
  })
  return unwrapList(data)
}

/** One protocol with its checklist items nested. Readable by any
 *  authenticated user — used by the doctor's "What to capture" panel. */
export async function getScreeningProtocol(id) {
  const { data } = await api.get(`/api/screening/protocols/${id}/`)
  return data
}

export async function createScreeningProtocol(payload) {
  const { data } = await api.post('/api/screening/protocols/', payload)
  return data
}

export async function updateScreeningProtocol(id, patch) {
  const { data } = await api.patch(`/api/screening/protocols/${id}/`, patch)
  return data
}

export async function deleteScreeningProtocol(id) {
  await api.delete(`/api/screening/protocols/${id}/`)
}

// ---- Protocol checklist items ----

export async function createScreeningProtocolItem(payload) {
  const { data } = await api.post('/api/screening/protocol-items/', payload)
  return data
}

export async function updateScreeningProtocolItem(id, patch) {
  const { data } = await api.patch(`/api/screening/protocol-items/${id}/`, patch)
  return data
}

export async function deleteScreeningProtocolItem(id) {
  await api.delete(`/api/screening/protocol-items/${id}/`)
}

// ---- Campaigns ----

export async function listScreeningCampaigns(params = {}) {
  const { data } = await api.get('/api/screening/campaigns/', {
    params: { page_size: 200, ...params },
  })
  return unwrapList(data)
}

export async function getScreeningCampaign(id) {
  const { data } = await api.get(`/api/screening/campaigns/${id}/`)
  return data
}

export async function createScreeningCampaign(payload) {
  const { data } = await api.post('/api/screening/campaigns/', payload)
  return data
}

export async function updateScreeningCampaign(id, patch) {
  const { data } = await api.patch(`/api/screening/campaigns/${id}/`, patch)
  return data
}

export async function deleteScreeningCampaign(id) {
  await api.delete(`/api/screening/campaigns/${id}/`)
}

/** Screen a patient into a campaign. body: { patient_id, doctor_id }. */
export async function startScreeningEncounter(campaignId, body) {
  const { data } = await api.post(
    `/api/screening/campaigns/${campaignId}/start_encounter/`, body)
  return data
}

export async function getScreeningCohortReport(campaignId) {
  const { data } = await api.get(
    `/api/screening/campaigns/${campaignId}/cohort_report/`)
  return data
}

// ---- Records ----

export async function listScreeningRecords(params = {}) {
  const { data } = await api.get('/api/screening/records/', {
    params: { page_size: 500, ...params },
  })
  return unwrapList(data)
}

export async function updateScreeningRecord(id, patch) {
  const { data } = await api.patch(`/api/screening/records/${id}/`, patch)
  return data
}

/** Remove a person from a campaign — deletes the screening record only;
 *  the underlying clinical encounter is left intact. */
export async function deleteScreeningRecord(id) {
  await api.delete(`/api/screening/records/${id}/`)
}

// ---- Fitness for Work PDF (shared document generator) ----

/** Generate a Fitness for Work report PDF for a screening encounter.
 *  The backend collector auto-fills employer / outcome / restrictions
 *  from the linked screening record; `payload` may add e.g. job_role. */
export async function generateFitnessReport(encounterId, payload = {}) {
  const { data } = await api.post('/api/documents/generate/', {
    document_type: 'fitness_for_work',
    encounter_id: encounterId,
    payload,
  })
  return data
}

// ---- Doctors (intake picker) ----

export async function listDoctorsForScreening() {
  const { data } = await api.get('/api/doctors/', {
    params: { page_size: 200 },
  })
  return unwrapList(data)
}
